import { forEach, isArray, isEmpty, isPlainObject, map } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import UIUtils from '../../../lib/UIUtils';
import { selectXPath } from 'js/lib/XMLUtils';
import cx from 'classnames';

const MAX_SUGGESTIONS = 5;
const MAX_XML_DEPTH = 2;

import styles from './DataPathSuggestions.module.scss';

function DataPathSuggestions({
  content,
  contentType,
  onSuggestionClicked,
  selectedPath,
  recommend = false,
}) {
  const suggestions = useMemo(() => _getSuggestions(content, contentType), [content, contentType]);

  // Recommended = the path that yields the most records; ties broken by the most data.
  // Opt-in (redesign only) so the legacy form's behavior is unchanged.
  const recommendedIndex = useMemo(
    () => (recommend ? _getRecommendedIndex(suggestions) : -1),
    [recommend, suggestions],
  );

  // Auto-select the recommended path when none is chosen yet (e.g. right after a Test).
  // Never overrides an existing selection (edit mode / a manual pick).
  useEffect(() => {
    if (recommend && !selectedPath && recommendedIndex >= 0 && suggestions?.[recommendedIndex]) {
      onSuggestionClicked(suggestions[recommendedIndex].dataPath);
    }
  }, [recommend, selectedPath, recommendedIndex, suggestions, onSuggestionClicked]);

  function _handleSuggestionClicked(event) {
    const { index } = event.currentTarget.dataset;

    onSuggestionClicked(suggestions[index].dataPath);
  }

  return (
    <div className={cx([styles.suggestions_container, 'suggestions-container'])}>
      <h4 className={cx([styles.suggestions_title, 'suggestions-title'])}>Suggestions</h4>
      <div className={cx([styles.inner_container, 'suggestions-inner-container'])}>
        {isEmpty(suggestions)
          ? 'Click "Test" above to fetch a sample API response and Nexla will auto-generate some suggestions for Path to Data.'
          : map(suggestions, ({ dataPath, description, numRecords }, index) => (
              <div
                key={index}
                data-index={index}
                className={cx([
                  styles.suggestion_item,
                  'suggestion-item',
                  { 'suggestion-item-active': selectedPath === dataPath },
                ])}
                onClick={_handleSuggestionClicked}
              >
                <span className="suggestion-path">{dataPath}</span>
                <div className="suggestion-description">{description}</div>
                <span className="num-records">{UIUtils.toPluralFormat('record', numRecords)}</span>
                {recommend && index === recommendedIndex && (
                  <span className={cx([styles.recommended_badge, 'suggestion-recommended'])}>
                    Suggested
                  </span>
                )}
              </div>
            ))}
      </div>
    </div>
  );
}

/** Rough byte-size of the data at a path — used only to break record-count ties. */
function _dataSize(value) {
  try {
    return typeof value === 'string' ? value.length : (JSON.stringify(value)?.length ?? 0);
  } catch (error) {
    return 0;
  }
}

/**
 * Index of the recommended suggestion: the one with the most records; ties broken by
 * the most data. Returns -1 when there are no suggestions.
 */
function _getRecommendedIndex(suggestions) {
  if (isEmpty(suggestions)) {
    return -1;
  }

  let best = -1;
  let bestRecords = -1;
  let bestSize = -1;

  forEach(suggestions, (suggestion, index) => {
    const records = suggestion.numRecords ?? 0;
    const size = suggestion.dataSize ?? 0;

    if (records > bestRecords || (records === bestRecords && size > bestSize)) {
      best = index;
      bestRecords = records;
      bestSize = size;
    }
  });

  return best;
}

function _getSuggestions(content, contentType) {
  if (!content) {
    return null;
  }

  const suggestions = [];

  switch (contentType) {
    case 'application/json':
      let parsedContent;

      try {
        parsedContent = JSON.parse(content);
      } catch (error) {
        return null;
      }

      suggestions.push({
        dataPath: '$',
        description: 'the whole response',
        numRecords: 1,
        dataSize: content.length,
      });

      if (isPlainObject(parsedContent)) {
        forEach(parsedContent, (value, key) => {
          if (suggestions.length >= MAX_SUGGESTIONS) {
            return false;
          }

          const isKeyWord = /^\w+$/.test(key);
          const dataPath = isKeyWord ? `$.${key}` : `$['${key}']`;

          if (isPlainObject(value)) {
            suggestions.push({
              dataPath,
              description: `the object "${key}"`,
              numRecords: 1,
              dataSize: _dataSize(value),
            });
          } else if (isArray(value)) {
            suggestions.push({
              dataPath: `${dataPath}[*]`,
              description: `all elements of the array "${key}"`,
              numRecords: value.length,
              dataSize: _dataSize(value),
            });
          }
        });
      } else if (isArray(parsedContent)) {
        suggestions.push({
          dataPath: '$[*]',
          description: 'all elements of the root array',
          numRecords: parsedContent.length,
          dataSize: content.length,
        });
      }

      break;

    case 'application/xml':
      suggestions.push({
        dataPath: '*',
        description: 'the root element',
        numRecords: 1,
      });
      _getXMLChildrenSuggestions(suggestions[0], 0, content, suggestions);

      break;

    default:
  }

  return suggestions;
}

function _getXMLChildrenSuggestions(parentSuggestion, parentDepth, xml, suggestions) {
  if (parentDepth >= MAX_XML_DEPTH) {
    return;
  }

  const childNodes = selectXPath(`${parentSuggestion.dataPath}/*`, xml);

  // count repititions of each tag
  const nodeCounts = {};

  forEach(childNodes, (node) => {
    if (!node.nodeName) {
      return;
    }

    if (!nodeCounts[node.nodeName]) {
      nodeCounts[node.nodeName] = 0;
    }

    nodeCounts[node.nodeName]++;
  });

  forEach(nodeCounts, (count, nodeName) => {
    const childPath = `${parentSuggestion.dataPath}/${nodeName}`;
    const childDescription = `<${nodeName}> children of ${parentSuggestion.description}`;
    const childSuggestion = {
      dataPath: childPath,
      description: childDescription,
      numRecords: count,
    };

    // add this node to suggestions if there are multiple tags with its name
    if (count > 1) {
      suggestions.push(childSuggestion);

      if (suggestions.length >= MAX_SUGGESTIONS) {
        return false;
      }
    }

    _getXMLChildrenSuggestions(childSuggestion, parentDepth + 1, xml, suggestions);
  });
}

export default DataPathSuggestions;
